import { useSelector } from 'react-redux';
import { readStates } from '@/constants/lifecycle';
import { findResultByEvent } from '@/utils/resultsHelper';
import pagingWithFilteredResults from '@/constants/states';
import allowNotificationsRead from '../helper';

const useNotificationDialogs = () => {
  const { role: { permissions: { datasets: { read }, users: { approve } } } } = useSelector(({ auth }) => auth);
  const { activeNotification, subscriptions } = useSelector(({ notifications }) => notifications);
  return {
    allowRead: activeNotification.open && read,
    allowSubscribe: subscriptions.open && approve,
  };
};

const useNotificationsActions = ({ category }) => {
  const { role } = useSelector(({ auth }) => auth);
  return { allowRefresh: allowNotificationsRead(category, role) };
};

const notificationsPanelState = ({
  results: notifications, name, readErrors, readStatus,
}) => ({
  notifications, name, readErrors, readStatus,
});

const findNotifications = (notifications, category, code, event) => (
  findResultByEvent(notifications, { category, code }, event) || {
    name: '', ...readStates, ...pagingWithFilteredResults(), isDefault: true,
  }
);

const notificationsPanelPaging = ({ currentPage, totalPages: pageCount, pageSize }) => ({
  currentPage, pageCount, pageSize,
});

const useNotificationsPanelNarrow = ({ category, code, event }) => useSelector(({
  notifications: { notifications },
}) => {
  const { name, readStatus } = findNotifications(notifications, category, code, event);
  return { name, readStatus };
});

const useNotificationsFilters = ({ category, code, event }) => useSelector(({ notifications: { notifications } }) => {
  const { filter: filterTerm, readStatus } = findNotifications(notifications, category, code, event);
  return { filterTerm, readStatus };
});

const useNotificationsPanelContainer = ({ category, code, event }) => useSelector(({
  auth: { role },
  notifications: { notifications },
}) => {
  const item = findNotifications(notifications, category, code, event);

  return {
    exists: !item.isDefault,
    canRead: item.isDefault ? false : allowNotificationsRead(category, role),
    ...notificationsPanelState(item),
    pageSize: notificationsPanelPaging(item).pageSize,
    total: item.count,
  };
});

const useNotificationsTable = ({ category, code, event }) => useSelector(({
  auth: { role: { permissions: { users: { read: allowUserLink } } } },
  notifications: { notifications },
}) => {
  const item = findNotifications(notifications, category, code, event);

  return {
    allowUserLink,
    ...notificationsPanelState(item),
    ...notificationsPanelPaging(item),
    total: item.count,
  };
});

const useNotificationCards = ({ category, code, event }) => useSelector(({ notifications: { notifications } }) => {
  const item = findNotifications(notifications, category, code, event);
  return { ...notificationsPanelState(item), ...notificationsPanelPaging(item) };
});

const useNotificationTools = () => {
  const { role: { permissions: { datasets: { read: allowRead } } } } = useSelector(({ auth }) => auth);
  return { allowRead };
};

const useShowNotificationDialog = () => {
  const { notification } = useSelector(({ notifications: { activeNotification } }) => activeNotification);
  const {
    author: { name: author }, createdOn, description, name: title,
  } = notification;
  return {
    author, createdOn, description, title,
  };
};

export default {
  useNotificationDialogs,
  useNotificationsActions,
  useNotificationCards,
  useNotificationsFilters,
  useNotificationsPanelContainer,
  useNotificationsPanelNarrow,
  useNotificationsTable,
  useNotificationTools,
  useShowNotificationDialog,
};
